import React from 'react';
import { useForm } from 'react-hook-form';
import {
  Alert,
  Box,
  Dialog,
  Portal,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactComponent as WithdrawIcon } from 'assets/icons/transfer.svg';
import { useTimer, useWithdrawSendOtp } from 'hooks';
import useInputAttributes from 'hooks/useInputAttributes';
import { useTranslation } from 'localization';

import {
  Button,
  DIALOG_ACTION_BUTTON_PROPS,
  Input,
  ServiceDialogBody,
  ServiceDialogCloseTrigger,
  ServiceDialogFooter,
  ServiceDialogHeader,
  serviceDialogShellProps,
} from 'components';
import { fixDecimal, yup } from 'utils';

type ApproveModalType = {
  onClose: () => void;
  isOpen: boolean;
  loading: boolean;
  data: {
    id: string;
    symbol: string;
    fee: number;
    amount: number;
    destination_amount: number;
  };
  // eslint-disable-next-line no-unused-vars
  onButton: (code: string) => void;
};

const ApproveModal: React.FC<ApproveModalType> = ({
  onClose,
  isOpen,
  data,
  loading,
  onButton,
}: ApproveModalType) => {
  const { t } = useTranslation('component.withdraw.modal');
  const { counter, setCounter } = useTimer(0);

  const schema = yup.object().shape({
    code: yup.string().label(t('form.code')).required(),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const onSubmit = async (data: { code: string }) => {
    onButton(data.code);
    reset();
  };

  const formProps = useInputAttributes(register, errors, {
    code: t('form.code'),
  });

  const { mutateAsync: requestToGetOtp, isPending: otpRequestLoading } =
    useWithdrawSendOtp();

  React.useEffect(() => {
    if (isOpen) {
      setCounter(120);
    }
  }, [isOpen]);

  return (
    <Box display="flex" alignItems="center">
      <Dialog.Root
        size="xl"
        open={isOpen}
        placement="center"
        onOpenChange={(e: { open: boolean }) => {
          if (!e.open) {
            reset();
            onClose();
          }
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content {...serviceDialogShellProps({ maxW: 'xl' })}>
              <ServiceDialogHeader>{t('confirm')}</ServiceDialogHeader>
              <ServiceDialogCloseTrigger />
              <ServiceDialogBody py={6} px={4}>
                <VStack align="center" justify="flex-start" h="100%" w="100%">
                  <VStack w="100%" align="center" justify="center" flex="auto">
                    <WithdrawIcon />
                    <Text fontSize="4xl" fontWeight="500">
                      {`${data.destination_amount} ${data.symbol}`}
                    </Text>
                  </VStack>
                  <Table.ScrollArea w="100%" overflowX="auto">
                    <Table.Root variant="line">
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell textAlign="left">
                            {t('amount')}
                          </Table.Cell>
                          <Table.Cell textAlign="right">
                            {`${data.amount} ${data.symbol}`}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell textAlign="left">{t('fee')}</Table.Cell>
                          <Table.Cell textAlign="right">
                            {fixDecimal({ value: data.fee })}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell textAlign="left">
                            {t('destination_amount')}
                          </Table.Cell>
                          <Table.Cell textAlign="right">
                            {`${data.destination_amount}  ${data.symbol}`}
                          </Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table.Root>
                  </Table.ScrollArea>
                  <Box w="100%">
                    <Alert.Root status="info">
                      <Text w="100%" textAlign="center" color="info">
                        {t('codeSent')}
                      </Text>
                    </Alert.Root>
                  </Box>
                  <Box w="100%">
                    <form
                      id="withdraw-bank-approve-form"
                      onSubmit={handleSubmit(onSubmit)}
                    >
                      <Box
                        w="100%"
                        display="flex"
                        flexDir={{ base: 'column', lg: 'row' }}
                        gap={4}
                      >
                        <Input
                          {...formProps.code}
                          textAlign="center"
                          placeholder="      -      -      -      -      -      -      "
                          type="number"
                          min="0"
                          maxLength="6"
                          minLength="6"
                        />
                        <Button
                          variant="filled"
                          display="block"
                          title={
                            counter > 0 ? '( ' + counter + ' )' : t('sendCode')
                          }
                          isDisabled={counter > 0}
                          width={{ base: '100%', lg: '200px' }}
                          isLoading={otpRequestLoading}
                          onClick={() =>
                            requestToGetOtp([]).then(() => {
                              setCounter(120);
                            })
                          }
                          mt={{ base: 0, lg: 8 }}
                          {...DIALOG_ACTION_BUTTON_PROPS}
                        />
                      </Box>
                    </form>
                  </Box>
                </VStack>
              </ServiceDialogBody>
              <ServiceDialogFooter>
                <Button
                  form="withdraw-bank-approve-form"
                  type="submit"
                  w="100%"
                  isLoading={loading}
                  variant="filled"
                  title={t('confirm')}
                  {...DIALOG_ACTION_BUTTON_PROPS}
                />
              </ServiceDialogFooter>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  );
};

export default ApproveModal;
